import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import FormRange from 'react-bootstrap/FormRange'

import { PinMapFill } from 'react-bootstrap-icons'

import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';

import { useState, useEffect, useRef, useCallback } from 'react';
import { dawaAutocomplete } from 'dawa-autocomplete2';

const DANISH_POSTCODE_PATTERN = /\b\d{4}\b/;
const GEOLOCATION_OPTIONS = {
    enableHighAccuracy: false,
    maximumAge: 300000,
    timeout: 15000,
};

const getGeoLocationErrorMessage = (error) => {
    if (error?.code === 1) {
        return 'Placering er blokeret. Tillad placering for Budgetvenlig i Safari-indstillingerne, og prøv igen.';
    }

    if (error?.code === 2) {
        return 'Din placering kunne ikke bestemmes. Kontrollér at Placeringstjenester er slået til, og prøv igen.';
    }

    if (error?.code === 3) {
        return 'Det tog for lang tid at finde din placering. Prøv igen.';
    }

    return 'Din placering kunne ikke hentes på denne enhed. Indtast din adresse i stedet.';
};

const hasDanishPostcode = (value) => {
    if (!value) {
        return false;
    }

    if (typeof value === 'object') {
        return Object.values(value).some(hasDanishPostcode);
    }

    return DANISH_POSTCODE_PATTERN.test(String(value));
};

const hasPostcodeInSuggestion = (selected) => {
    const data = selected?.data || {};
    const adgangsadresse = data.adgangsadresse || selected?.adgangsadresse || {};
    const adresse = data.adresse || selected?.adresse || {};

    return [
        selected?.tekst,
        selected?.postnr,
        selected?.postnummer,
        data.postnr,
        data.postnummer,
        data.postnrnavn,
        data.postnummernavn,
        adresse.postnr,
        adresse.postnummer,
        adresse.postnrnavn,
        adgangsadresse.postnr,
        adgangsadresse.postnummer,
        adgangsadresse.postnrnavn,
    ].some(hasDanishPostcode);
};

const toCoordinateString = (value) => {
    const number = Number(value);

    return Number.isFinite(number) ? String(number) : '';
};

const getPointCoordinates = (point) => {
    const coordinates = Array.isArray(point?.koordinater)
        ? point.koordinater
        : null;

    if (!coordinates || coordinates.length < 2) {
        return null;
    }

    const lon = toCoordinateString(coordinates[0]);
    const lat = toCoordinateString(coordinates[1]);

    return lat && lon ? { lat, lon } : null;
};

const getXyCoordinates = (value) => {
    const lon = toCoordinateString(value?.x);
    const lat = toCoordinateString(value?.y);

    return lat && lon ? { lat, lon } : null;
};

const getCoordinatesFromSuggestion = (selected) => {
    const data = selected?.data || {};
    const adgangsadresse = data.adgangsadresse || selected?.adgangsadresse || {};

    return getPointCoordinates(adgangsadresse?.adgangspunkt)
        || getPointCoordinates(data?.adgangspunkt)
        || getPointCoordinates(selected?.adgangspunkt)
        || getXyCoordinates(data)
        || getXyCoordinates(selected)
        || getXyCoordinates(adgangsadresse)
        || null;
};

const getLookupRequestKey = (criteria) => {
    const lat = String(criteria?.geoLocation?.lat || '').trim();
    const lon = String(criteria?.geoLocation?.lon || '').trim();

    if (!lat || !lon) {
        return '';
    }

    return `${lat}:${lon}:${Number(criteria?.radius) || 2}`;
};

const AddressSelector = ({
    initialCriteria,
    onCriteriaChange,
    onLocationLookupRequest,
    onAddressConfirmationChange,
    onRadiusChange,
}) => {
    const initialRadius = Number(initialCriteria?.radius);
    const normalizedInitialCriteria = {
        address: String(initialCriteria?.address || ''),
        radius: Number.isFinite(initialRadius) ? Math.max(1, Math.min(20, initialRadius)) : 2,
        geoLocation: initialCriteria?.geoLocation?.lat && initialCriteria?.geoLocation?.lon
            ? {
                lat: String(initialCriteria.geoLocation.lat),
                lon: String(initialCriteria.geoLocation.lon),
            }
            : {},
    };
    const [range, setRange] = useState(normalizedInitialCriteria.radius);
    const [address, setAddress] = useState(normalizedInitialCriteria.address);
    const [geoLocation, setGeoLocation] = useState(normalizedInitialCriteria.geoLocation);
    const [geoLocationStatus, setGeoLocationStatus] = useState({ state: 'idle', message: '' });
    const inputRef = useRef(null);
    const latestLocationLookupRequestRef = useRef(onLocationLookupRequest);
    const latestAddressConfirmationChangeRef = useRef(onAddressConfirmationChange);
    const lastLookupRequestKeyRef = useRef('');
    const radiusLookupTimerRef = useRef(null);
    const latestCriteriaRef = useRef(normalizedInitialCriteria);

    useEffect(() => {
        latestLocationLookupRequestRef.current = onLocationLookupRequest;
    }, [onLocationLookupRequest]);

    useEffect(() => {
        latestAddressConfirmationChangeRef.current = onAddressConfirmationChange;
    }, [onAddressConfirmationChange]);

    const updateLatestCriteria = (nextCriteria) => {
        latestCriteriaRef.current = {
            ...latestCriteriaRef.current,
            ...nextCriteria,
        };
    };

    const updateAddressConfirmation = useCallback((isConfirmed) => {
        if (typeof latestAddressConfirmationChangeRef.current === 'function') {
            latestAddressConfirmationChangeRef.current(isConfirmed);
        }
    }, []);

    const requestLocationLookup = useCallback((reason) => {
        const criteria = latestCriteriaRef.current;
        const hasValidAddress = String(criteria.address || '').trim().length > 0;
        const hasValidCoordinates = Boolean(criteria.geoLocation?.lat && criteria.geoLocation?.lon);
        const lookupRequestKey = getLookupRequestKey(criteria);

        if (
            !hasValidAddress
            || !hasValidCoordinates
            || !lookupRequestKey
            || lookupRequestKey === lastLookupRequestKeyRef.current
            || typeof latestLocationLookupRequestRef.current !== 'function'
        ) {
            return;
        }

        lastLookupRequestKeyRef.current = lookupRequestKey;
        latestLocationLookupRequestRef.current(criteria, reason);
    }, []);

    const cancelScheduledRadiusLookup = useCallback(() => {
        if (radiusLookupTimerRef.current !== null) {
            window.clearTimeout(radiusLookupTimerRef.current);
            radiusLookupTimerRef.current = null;
        }
    }, []);

    const scheduleRadiusLookup = useCallback(() => {
        cancelScheduledRadiusLookup();
        radiusLookupTimerRef.current = window.setTimeout(() => {
            radiusLookupTimerRef.current = null;
            requestLocationLookup('radius-change');
        }, 220);
    }, [cancelScheduledRadiusLookup, requestLocationLookup]);

    useEffect(() => cancelScheduledRadiusLookup, [cancelScheduledRadiusLookup]);

    useEffect(() => {
        if (!inputRef.current) {
            return undefined;
        }

        const autocomplete = dawaAutocomplete(inputRef.current, {
            minLength: 2,
            params: {
                per_side: 3,
            },
            select: (selected) => {
                const selectedText = selected?.tekst || '';
                setAddress(selectedText);

                const nextGeoLocation = getCoordinatesFromSuggestion(selected);

                if (nextGeoLocation) {
                    const hasCompleteSuggestedAddress = Boolean(selectedText.trim())
                        && hasPostcodeInSuggestion(selected);

                    updateLatestCriteria({
                        address: selectedText,
                        geoLocation: nextGeoLocation,
                    });
                    updateAddressConfirmation(hasCompleteSuggestedAddress);
                    setGeoLocation(nextGeoLocation);

                    if (hasCompleteSuggestedAddress) {
                        requestLocationLookup('address-select');
                        window.setTimeout(() => inputRef.current?.blur(), 0);
                    }
                    return;
                }

                updateLatestCriteria({
                    address: selectedText,
                    geoLocation: {},
                });
                updateAddressConfirmation(false);
                lastLookupRequestKeyRef.current = '';
                setGeoLocation({});
            },
        });

        return () => {
            if (autocomplete && typeof autocomplete.destroy === 'function') {
                autocomplete.destroy();
            }
        };
    }, [requestLocationLookup, updateAddressConfirmation]);

    useEffect(() => {
        if (typeof onCriteriaChange !== 'function') {
            return;
        }
        updateLatestCriteria({
            address,
            radius: range,
            geoLocation,
        });
        onCriteriaChange({
            address,
            radius: range,
            geoLocation,
        });
    }, [address, range, geoLocation, onCriteriaChange]);

    function requestGeoLocation() {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            setGeoLocationStatus({
                state: 'error',
                message: 'Din browser understøtter ikke automatisk placering. Indtast din adresse i stedet.',
            });
            return;
        }

        setGeoLocationStatus({ state: 'loading', message: 'Finder din placering…' });

        navigator.geolocation.getCurrentPosition(async (position) => {
            const nextGeoLocation = {
                lat: String(position.coords.latitude),
                lon: String(position.coords.longitude),
            };
            const fallbackAddress = address.trim() || 'Min nuværende placering';

            updateLatestCriteria({
                address: fallbackAddress,
                geoLocation: nextGeoLocation,
            });
            updateAddressConfirmation(true);
            setAddress(fallbackAddress);
            setGeoLocation(nextGeoLocation);
            setGeoLocationStatus({ state: 'success', message: 'Din placering er fundet.' });
            requestLocationLookup('location-button');

            try {
                const params = new URLSearchParams({
                    lat: nextGeoLocation.lat,
                    lon: nextGeoLocation.lon,
                    format: 'json',
                    'accept-language': 'da',
                });
                const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params.toString()}`);

                if (!response.ok) {
                    return;
                }

                const data = await response.json();
                const reverseAddress = data.address || {};
                const road = reverseAddress.road || reverseAddress.pedestrian || '';
                const locality = reverseAddress.suburb
                    || reverseAddress.city
                    || reverseAddress.town
                    || reverseAddress.village
                    || '';
                const resolvedAddress = [
                    [road, reverseAddress.house_number].filter(Boolean).join(' '),
                    reverseAddress.postcode,
                    locality,
                ].filter(Boolean).join(', ') || data.display_name || fallbackAddress;

                updateLatestCriteria({ address: resolvedAddress });
                setAddress(resolvedAddress);
            } catch {
                // Coordinates are enough to find nearby stores, so reverse
                // address lookup failure must not make geolocation fail.
            }
        }, (error) => {
            setGeoLocationStatus({ state: 'error', message: getGeoLocationErrorMessage(error) });
        }, GEOLOCATION_OPTIONS);
    }

    return (
    
    <Container className="address-selector-card">
        <Row className="justify-content-center">
            <Col xs={10}>
                <div className="mb-3 address-input-row">
                    <div className="autocomplete-container address-autocomplete-container">
                        <Form.Control
                            ref={inputRef}
                            placeholder="Indtast adresse (finder butikker nær dig)"
                            aria-label="Address"
                            aria-describedby="pinMapButton"
                            value={address}
                            onChange={(e) => {
                                const nextAddress = e.target.value;
                                updateLatestCriteria({
                                    address: nextAddress,
                                    geoLocation: {},
                                });
                                updateAddressConfirmation(false);
                                lastLookupRequestKeyRef.current = '';
                                setAddress(nextAddress);
                                setGeoLocation({});
                            }}
                            onBlur={() => {
                                window.setTimeout(() => requestLocationLookup('address-blur'), 0);
                            }}
                        />
                    </div>
                    <Button
                        type="button"
                        onClick={requestGeoLocation}
                        variant="outline-secondary"
                        id="pinMapButton"
                        aria-label="Brug min placering"
                        title="Brug min placering"
                        disabled={geoLocationStatus.state === 'loading'}
                    >
                        <PinMapFill />
                    </Button>
                </div>
                {geoLocationStatus.message && (
                    <p
                        className={`address-location-status is-${geoLocationStatus.state}`}
                        role={geoLocationStatus.state === 'error' ? 'alert' : 'status'}
                        aria-live="polite"
                    >
                        {geoLocationStatus.message}
                    </p>
                )}
            </Col>
        </Row>
        <Row>
            <Col className="range-readout">{Number(range).toFixed(1)} km</Col>
        </Row>
        <Row>
            <Col xs={10} className="mx-auto">
                <FormRange
                    className="radius-range"
                    value={range}
                    min={1}
                    max={20}
                    step={0.1}
                    onChange={(e) => {
                        const nextRange = parseFloat(e.target.value);
                        updateLatestCriteria({ radius: nextRange });
                        setRange(nextRange);
                        if (typeof onRadiusChange === 'function') {
                            onRadiusChange(latestCriteriaRef.current);
                        }
                        scheduleRadiusLookup();
                    }}
                    onBlur={() => {
                        cancelScheduledRadiusLookup();
                        requestLocationLookup('radius-blur');
                    }}
                />
            </Col>
        </Row>
    </Container>

    );

}

export default AddressSelector;
